import { useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { ownerNav } from "@/config/nav";
import api from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function OwnerCalendar() {
  const [data, setData] = useState({ events: [], properties: [] });
  const [propId, setPropId] = useState("");
  const [selected, setSelected] = useState(null);

  const load = (pid) => {
    const url = pid ? `/owner/calendar?property_id=${pid}` : "/owner/calendar";
    api.get(url).then((r) => {
      setData(r.data);
      if (!pid && r.data.properties[0]) setPropId(r.data.properties[0].id);
    });
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (propId) load(propId); /* eslint-disable-next-line */ }, [propId]);

  const eventMap = {};
  data.events.forEach((e) => { eventMap[e.date] = e.status; });

  const statusFor = (d) => eventMap[format(d, "dd/MM/yyyy")];

  const toggleBlock = async (d) => {
    if (!propId) return toast.error("Select a property first");
    const ds = format(d, "dd/MM/yyyy");
    const st = eventMap[ds];
    if (st === "confirmed" || st === "pending") return toast.error("This date has a booking and cannot be blocked/unblocked.");
    const r = await api.post("/owner/block-date", { property_id: propId, date: ds });
    toast.success(r.data.blocked ? "Date blocked" : "Date unblocked");
    load(propId);
  };

  const Legend = ({ color, label }) => (
    <div className="flex items-center gap-2 text-sm text-stone-600"><span className={`h-3 w-3 rounded-full ${color}`} /> {label}</div>
  );

  return (
    <DashboardLayout nav={ownerNav} title="Owner">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Calendar</h1>
        <Select value={propId} onValueChange={setPropId}>
          <SelectTrigger data-testid="cal-property" className="w-64 rounded-xl"><SelectValue placeholder="Select property" /></SelectTrigger>
          <SelectContent>{data.properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <p className="mt-2 text-sm text-stone-500">Tap a date to block/unblock it. Confirmed bookings are automatically unavailable.</p>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row">
        <div className="rounded-2xl border border-stone-200 bg-white p-4">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => { if (d) { setSelected(d); toggleBlock(d); } }}
            modifiers={{
              blocked: (d) => statusFor(d) === "blocked",
              confirmed: (d) => statusFor(d) === "confirmed",
              pending: (d) => statusFor(d) === "pending",
            }}
            modifiersClassNames={{
              blocked: "bg-stone-300 text-stone-700 rounded-md",
              confirmed: "bg-green-600 text-white rounded-md",
              pending: "bg-amber-400 text-white rounded-md",
            }}
          />
        </div>
        <div className="space-y-3 rounded-2xl border border-stone-200 bg-white p-6">
          <h2 className="font-heading font-semibold">Legend</h2>
          <Legend color="bg-white border border-stone-300" label="Available" />
          <Legend color="bg-amber-400" label="Pending booking" />
          <Legend color="bg-green-600" label="Confirmed booking" />
          <Legend color="bg-stone-300" label="Blocked by you" />
        </div>
      </div>
    </DashboardLayout>
  );
}
